<template>
    <div>
        <section class="breadcrumb-section embroidery_breadcrumb">
            <div class="container">
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link to="/">Home</router-link>
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">
                            {{ currentTabLabel }}
                        </li>
                    </ol>
                </nav>
            </div>
        </section>

        <section class="embroidery_headings pb_50 pt-0">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="section_heading_box embroidery_sec_heading">
                            <h1 class="heading1 section_heading position-relative text-center mb-0">
                                <span class="bg_secondary_white px_40">{{ currentTabLabel }} Gallery</span>
                            </h1>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12 d-md-flex justify-content-center align-items-center embroidery_buttons">
                        <ul class="tab_one footer-gallery-tabs-scroll" @wheel.prevent="onTabsWheel">
                            <li
                                v-for="tab in tabs"
                                :key="tab.section"
                                :class="activeTab === tab.section ? 'active' : ''"
                            >
                                <router-link
                                    :to="{
                                        name: 'FooterGallerySection',
                                        params: { section: tab.section }
                                    }"
                                >
                                    {{ tab.label }}
                                </router-link>
                            </li>
                        </ul>
                        <router-link
                            to="/design-studio/v3"
                            class="btn btn_orange bg_primary_orange text_white ml-auto py_10 px_24"
                        >Start {{ currentTabLabel }}</router-link>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="loading" class="video_banner p-0">
            <div class="container text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </section>

        <template v-else>
            <!-- Banner: only when admin uploaded a banner and/or a video URL. No default stock images. -->
            <section v-if="showBannerSection" class="video_banner p-0">
                <div class="">
                    <div class="col-md-12 p-0 position-relative banner-image-wrap">
                        <img
                            v-if="bannerDisplayUrl"
                            :src="bannerDisplayUrl"
                            alt=""
                            class="video_banner w-100 banner-image"
                        />
                        <div
                            v-else-if="hasVideo"
                            class="video_banner w-100 banner-image banner-video-thumb-fallback"
                            role="img"
                            aria-label="Video thumbnail"
                        />
                        <div v-if="hasVideo" class="play_button">
                            <a href="javascript:void(0)" @click.prevent="onPlayClick">
                                <img src="/maincss/images/play_icon.png" alt="" class="w-auto play_image" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Video playback popup -->
            <div v-if="videoPopupVisible" class="video-popup-overlay" @click.self="closeVideoPopup" @touchmove.self.prevent>
                <div class="video-popup-box">
                    <button type="button" class="video-popup-close" aria-label="Close" @click="closeVideoPopup">&times;</button>
                    <div v-if="videoSource && isEmbedUrl(videoSource)" class="embed-responsive embed-responsive-16by9">
                        <iframe
                            :src="embedUrl(videoSource)"
                            class="embed-responsive-item"
                            allowfullscreen
                        ></iframe>
                    </div>
                    <video
                        v-else-if="videoSource && isVideoUrl(videoSource)"
                        :src="videoSource"
                        controls
                        playsinline
                        class="w-100"
                        style="max-height: 80vh;"
                    ></video>
                    <div v-else-if="videoSource" class="embed-responsive embed-responsive-16by9">
                        <iframe
                            v-if="embedUrl(videoSource)"
                            :src="embedUrl(videoSource)"
                            class="embed-responsive-item"
                            allowfullscreen
                        ></iframe>
                    </div>
                </div>
            </div>

            <section class="embroidery_content">
                <div class="container">
                    <div class="row">
                        <div
                            v-for="(img, idx) in sectionData.images"
                            :key="img.id || idx"
                            class="col-md-4 col-6 col-lg-3"
                        >
                            <div class="embroidery_product position-relative mb_60">
                                <img :src="img.image_path" :alt="'Gallery image ' + (idx + 1)" class="w-100" />
                            </div>
                        </div>
                    </div>
                    <p v-if="sectionData.images.length === 0 && !loading" class="text-muted text-center py-4">No gallery images yet.</p>
                    <nav
                        v-if="imagesPagination.total_pages > 1"
                        class="footer-gallery-pagination"
                        aria-label="Gallery pagination"
                    >
                        <button
                            type="button"
                            class="footer-gallery-pagination__prev"
                            :disabled="currentPage <= 1 || loading"
                            @click="goToPage(currentPage - 1)"
                        >
                            <span class="footer-gallery-pagination__arrow" aria-hidden="true">←</span>
                            Previous
                        </button>
                        <button
                            v-for="p in visiblePageNumbers"
                            :key="'p-' + p"
                            type="button"
                            class="footer-gallery-pagination__num"
                            :class="{ 'footer-gallery-pagination__num--active': p === currentPage }"
                            :disabled="loading"
                            @click="goToPage(p)"
                        >
                            {{ p }}
                        </button>
                        <button
                            type="button"
                            class="footer-gallery-pagination__next"
                            :disabled="currentPage >= imagesPagination.total_pages || loading"
                            @click="goToPage(currentPage + 1)"
                        >
                            Next
                            <span class="footer-gallery-pagination__arrow" aria-hidden="true">→</span>
                        </button>
                    </nav>
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import ApiService from "@/common/api.service";

export default {
    name: "FooterGallery",
    props: {
        initialSection: {
            type: String,
            default: "design_templates"
        }
    },
    data() {
        return {
            tabs: [],
            activeTab: this.initialSection,
            loading: true,
            currentPage: 1,
            imagesPerPage: 12,
            imagesPagination: {
                page: 1,
                per_page: 12,
                total: 0,
                total_pages: 1
            },
            sectionData: {
                video: null,
                images: []
            },
            videoPopupVisible: false
        };
    },
    computed: {
        currentTabLabel() {
            const t = this.tabs.find((x) => x.section === this.activeTab);
            if (t) return t.label;
            return this.formatSectionLabel(this.activeTab) || "Gallery";
        },
        videoSource() {
            const v = this.sectionData.video;
            return (v && (v.video_url || v.video_file_path)) || "";
        },
        hasVideo() {
            return !!this.videoSource;
        },
        /** Admin-uploaded banner only (no defaults per section). */
        hasBanner() {
            const v = this.sectionData.video;
            const url = (v && v.banner_url) || "";
            return !!(url && String(url).trim());
        },
        /** Show hero area only if there is a banner and/or a video to present. */
        showBannerSection() {
            return this.hasBanner || this.hasVideo;
        },
        /**
         * Image shown in hero: banner URL if set; else video thumbnail (YouTube/Vimeo) when only video exists.
         */
        bannerDisplayUrl() {
            if (this.hasBanner) {
                return String(this.sectionData.video.banner_url).trim();
            }
            if (this.hasVideo) {
                return this.videoThumbnailFromUrl(this.videoSource) || "";
            }
            return "";
        },
        visiblePageNumbers() {
            const total = this.imagesPagination.total_pages || 1;
            const cur = this.currentPage;
            if (total <= 7) {
                return Array.from({ length: total }, (_, i) => i + 1);
            }
            let start = Math.max(1, cur - 2);
            let end = Math.min(total, cur + 2);
            if (end - start < 4) {
                if (start === 1) end = Math.min(total, start + 4);
                else if (end === total) start = Math.max(1, end - 4);
            }
            const pages = [];
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        }
    },
    watch: {
        $route: {
            handler(route) {
                const section =
                    (route && route.params && route.params.section) ||
                    this.sectionFromPath(route && route.path);
                if (section && section !== this.activeTab) {
                    this.activeTab = section;
                    this.currentPage = 1;
                    this.loadSection();
                }
            },
            immediate: false
        },
        initialSection(val) {
            if (val && val !== this.activeTab) {
                this.activeTab = val;
                this.currentPage = 1;
                this.loadSection();
            }
        },
        videoPopupVisible(visible) {
            if (visible) {
                document.body.style.overflow = "hidden";
                document.body.style.position = "fixed";
                document.body.style.width = "100%";
            } else {
                document.body.style.overflow = "";
                document.body.style.position = "";
                document.body.style.width = "";
            }
        }
    },
    mounted() {
        this.activeTab = this.initialSection;
        this.loadSections().then(() => {
            this.currentPage = 1;
            this.loadSection();
        });
        document.body.classList.remove("bg_primary_light_white");
    },
    methods: {
        formatSectionLabel(sectionValue) {
            if (!sectionValue) return "";
            const s = String(sectionValue).trim();

            // Backward compatible labels for existing seeded slugs
            if (s === "design_templates") return "Design Template";
            if (s === "screen_printing") return "Screen Print";
            if (s === "embroidery") return "Embroidery";

            // Generic formatting: snake_case -> Title Case
            return s
                .replace(/_/g, " ")
                .replace(/-/g, " ")
                .split(" ")
                .filter(Boolean)
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ");
        },
        async loadSections() {
            try {
                const res = await ApiService.query("footer-gallery/public-sections", {
                    params: {}
                });
                if (res.data && res.data.success) {
                    const sections = res.data.sections || [];
                    this.tabs = sections.map((s) => ({
                        section: s.section,
                        label: this.formatSectionLabel(s.section)
                    }));

                    if (this.tabs.length) {
                        // Keep current active tab if it exists, otherwise fall back to first.
                        if (!this.tabs.some((t) => t.section === this.activeTab)) {
                            this.activeTab = this.tabs[0].section;
                        }
                    }
                }
            } catch (e) {
                console.error("Footer gallery load sections error:", e);
            }
        },
        sectionFromPath(path) {
            if (path === "/design-templates") return "design_templates";
            if (path === "/screen-printing") return "screen_printing";
            if (path === "/embroidery") return "embroidery";
            return null;
        },
        async loadSection() {
            this.loading = true;
            this.sectionData = { video: null, images: [] };
            try {
                const res = await ApiService.query("footer-gallery/public", {
                    params: {
                        section: this.activeTab,
                        page: this.currentPage,
                        per_page: this.imagesPerPage
                    }
                });
                if (res.data && res.data.success) {
                    this.sectionData = {
                        video: res.data.video || null,
                        images: res.data.images || []
                    };
                    const pg = res.data.images_pagination;
                    if (pg && typeof pg.total_pages === "number") {
                        this.imagesPagination = {
                            page: pg.page,
                            per_page: pg.per_page,
                            total: pg.total,
                            total_pages: Math.max(1, pg.total_pages)
                        };
                        this.currentPage = pg.page;
                    } else {
                        this.imagesPagination = {
                            page: 1,
                            per_page: this.imagesPerPage,
                            total: (this.sectionData.images || []).length,
                            total_pages: 1
                        };
                    }
                }
            } catch (e) {
                console.error("Footer gallery load error:", e);
            } finally {
                this.loading = false;
            }
        },
        goToPage(page) {
            const p = Math.max(1, Math.min(page, this.imagesPagination.total_pages || 1));
            if (p === this.currentPage) return;
            this.currentPage = p;
            this.loadSection();
            const el = document.querySelector(".embroidery_content");
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
        },
        onTabsWheel(event) {
            const el = event.currentTarget;
            if (!el) return;
            const delta = event.deltaY || event.deltaX || 0;
            el.scrollLeft += delta;
        },
        isVideoUrl(url) {
            if (!url) return false;
            return /\.(mp4|webm|ogg)(\?|$)/i.test(url) || url.includes("video");
        },
        isEmbedUrl(url) {
            if (!url) return false;
            return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
        },
        embedUrl(url) {
            if (!url) return "";
            let id = null;
            if (url.includes("youtube.com/watch")) {
                const m = url.match(/[?&]v=([^&]+)/);
                id = m ? m[1] : null;
                return id ? `https://www.youtube.com/embed/${id}` : url;
            }
            if (url.includes("youtu.be/")) {
                const m = url.match(/youtu\.be\/([^?]+)/);
                id = m ? m[1] : null;
                return id ? `https://www.youtube.com/embed/${id}` : url;
            }
            if (url.includes("vimeo.com")) {
                const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                id = m ? m[1] : null;
                return id ? `https://player.vimeo.com/video/${id}` : url;
            }
            return url;
        },
        onPlayClick() {
            if (this.hasVideo) this.videoPopupVisible = true;
        },
        /**
         * Thumbnail for video-only hero (no admin banner). YouTube/Vimeo; direct files use empty → CSS fallback.
         */
        videoThumbnailFromUrl(url) {
            if (!url || typeof url !== "string") return "";
            const u = url.trim();
            if (/youtube\.com|youtu\.be/i.test(u)) {
                let id = null;
                if (u.includes("youtube.com/watch")) {
                    const m = u.match(/[?&]v=([^&]+)/);
                    id = m ? m[1] : null;
                } else if (u.includes("youtu.be/")) {
                    const m = u.match(/youtu\.be\/([^?]+)/);
                    id = m ? m[1] : null;
                }
                return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
            }
            if (/vimeo\.com/i.test(u)) {
                const m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                const id = m ? m[1] : null;
                if (id) return `https://vumbnail.com/${id}.jpg`;
            }
            return "";
        },
        closeVideoPopup() {
            this.videoPopupVisible = false;
        }
    }
};
</script>

<style scoped>
.video-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    overflow: auto;
    overscroll-behavior: contain;
    touch-action: none;
}
.video-popup-box {
    position: relative;
    width: 100%;
    max-width: 900px;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
}
.video-popup-close {
    position: absolute;
    top: 8px;
    right: 12px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border: none;
    font-size: 28px;
    line-height: 1;
    padding: 0 8px;
    cursor: pointer;
    border-radius: 4px;
}
.video-popup-close:hover {
    background: rgba(0, 0, 0, 0.9);
}
.video_banner_wrapper {
    background: #000;
}
.embed-responsive-16by9 {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    overflow: hidden;
}
.embed-responsive-16by9::before {
    content: "";
    display: block;
    padding-top: 56.25%;
}
.embed-responsive-item {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

/* Banner image: fixed height so default and uploaded banner display the same size */
.banner-image-wrap {
    overflow: hidden;
    position: relative;
    /* Fixed hero height (banner or video-thumbnail treated as banner) */
    height: 501.03px;
    width: 100%;
}
.banner-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}
/* When only a direct video file (no embed thumbnail): neutral placeholder behind play icon */
.banner-video-thumb-fallback {
    background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
    min-height: 100%;
}


/* Gallery pagination (12 per page) */
.footer-gallery-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: 10px;
    margin-top: 2rem;
    margin-bottom: 1rem;
}
.footer-gallery-pagination button {
    font-family: inherit;
    font-size: 0.9375rem;
    line-height: 1.4;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.footer-gallery-pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.footer-gallery-pagination__prev {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0.45rem 0.85rem;
    color: #333;
    background: #fff;
    border: 1px solid #d0d0d0;
}
.footer-gallery-pagination__prev:hover:not(:disabled) {
    background: #f8f8f8;
    border-color: #bbb;
}
.footer-gallery-pagination__num {
    min-width: 2.25rem;
    height: 2.25rem;
    padding: 0 0.35rem;
    color: #333;
    background: #fff;
    border: 1px solid #d0d0d0;
}
.footer-gallery-pagination__num:hover:not(:disabled):not(.footer-gallery-pagination__num--active) {
    background: #f8f8f8;
}
.footer-gallery-pagination__num--active {
    color: #fff;
    background: #f2790c;
    border-color: #f2790c;
    cursor: default;
}
.footer-gallery-pagination__next {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0.45rem 0.85rem;
    color: #fff;
    background: #f2790c;
    border: 1px solid #f2790c;
}
.footer-gallery-pagination__next:hover:not(:disabled) {
    background: #d96a0a;
    border-color: #d96a0a;
}
.footer-gallery-pagination__arrow {
    font-size: 0.875em;
}

/* Section tabs: one line + horizontal scroll */
.footer-gallery-tabs-scroll {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    margin-right: 12px;
}
.footer-gallery-tabs-scroll li {
    flex: 0 0 auto;
}
/* Keep Start button original size/look in flex row */
.embroidery_buttons .btn_orange {
    flex-shrink: 0;
    white-space: nowrap;
    position: relative;
    top: -6px;
}
</style>
